import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldLabel from './FieldLabel';
import FieldError from './FieldError';
import { RadioOption } from './FormRadioGroup';
import { twMerge } from 'tailwind-merge';
import { capitalizeFirstWord } from 'utilities/functions';

type ControlledRadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  className?: string;
  isRequired?: boolean;
  options: RadioOption[];
  disabled?: boolean;
};

interface RadioGroupProps {
  name: string;
  option: RadioOption;
  selectedValue: string | null;
  onChange: (value: string | number) => void;
  error?: string | null;
  disabled?: boolean;
}

const FormRadioCards: React.FC<RadioGroupProps> = ({
  name,
  option,
  selectedValue,
  onChange,
  error,
  disabled,
}) => {
  const isSelected = selectedValue === option.value;
  const isDisabled = disabled || option.disabled;

  return (
    <label
      key={option.value}
      className={twMerge(
        'flex cursor-pointer items-center w-full p-4 rounded-xl border transition-all duration-200',
        'text-base font-medium text-med-gray-3',
        isSelected
          ? 'border-clc-blue'
          : 'border-gray-opacity-15 hover:border-gray-300',
        error && 'border-alert-negative bg-error-yellow',
        isDisabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <input
        name={name}
        type="radio"
        disabled={isDisabled}
        value={option.value}
        checked={isSelected}
        onChange={() => onChange(option.value)}
        className={twMerge(
          'appearance-none flex cursor-pointer shrink-0 h-7 w-7 rounded-full mr-4 border bg-white',
          'border-neutral-500',
          'checked:border-clc-blue checked:border-[9px]',
          error && 'border-alert-negative border-2',
        )}
      />
      {capitalizeFirstWord(option.label)}
    </label>
  );
};

export const ControlledRadioCardGroup = <T extends FieldValues>({
  name,
  label,
  isRequired,
  options,
  disabled,
  className,
}: ControlledRadioGroupProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className="flex w-full flex-col gap-0.5">
      <FieldLabel label={label} isRequired={isRequired} name={name} />
      <div className={twMerge(className)}>
        {options.map((option) => (
          <FormRadioCards
            option={option}
            onChange={field.onChange}
            name={name}
            selectedValue={field.value}
            disabled={disabled}
            error={error?.message}
          />
        ))}
      </div>
      {error && <FieldError>{error.message}</FieldError>}
    </div>
  );
};

export default ControlledRadioCardGroup;
